import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { detectEcommercePlatform } from '@/shared/connectionPlatform';

interface AmazonFinanceSourceSettingsProps {
  organisationId: string;
  currentValue: string | null | undefined;
  onUpdate: () => void;
  /** Read-only, e.g. while merchant Settings admin mode is off (FIG-345). */
  disabled?: boolean;
}

const SOURCE_OPTIONS = [
  { value: 'legacy', label: 'Finance Events tables (legacy)' },
  { value: 'transactions', label: 'Transactions tables (new)' },
] as const;

export function displayedAmazonFinanceSource(
  value: string | null | undefined,
): 'legacy' | 'transactions' {
  return value === 'transactions' ? 'transactions' : 'legacy';
}

export function amazonSchemaReportPillLabel(value: string | null | undefined): string {
  return displayedAmazonFinanceSource(value) === 'transactions'
    ? 'Amazon schema: Transactions (new)'
    : 'Amazon schema: Finance Events (legacy)';
}

/** The switch applies only when the eight transaction tables are synced. */
export function amazonFinanceSwitchVisible(tablesReady: boolean): boolean {
  return tablesReady;
}

export function organisationHasAmazonSellingPartner(connections: unknown): boolean {
  const list = Array.isArray(connections)
    ? connections
    : connections && typeof connections === 'object'
      ? Object.values(connections)
      : [];
  return list.some((connection) => {
    if (!connection || typeof connection !== 'object') return false;
    const record = connection as { type?: string | null; service?: string | null; connector_name?: string | null };
    return detectEcommercePlatform(record) === 'amazon';
  });
}

export const AmazonFinanceSourceSettings: React.FC<AmazonFinanceSourceSettingsProps> = ({
  organisationId,
  currentValue,
  onUpdate,
  disabled = false,
}) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [tablesReady, setTablesReady] = useState<boolean | null>(null);
  const source = displayedAmazonFinanceSource(currentValue);

  useEffect(() => {
    let cancelled = false;
    invokeFunction<{ tablesReady?: boolean }>(supabase, 'amazon-finance-readiness-v1', {
      body: { organisation_id: organisationId },
    })
      .then(({ data }) => {
        if (!cancelled) setTablesReady(data?.tablesReady === true);
      })
      .catch(() => {
        if (!cancelled) setTablesReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId]);
  const selectedLabel =
    SOURCE_OPTIONS.find((option) => option.value === source)?.label ?? SOURCE_OPTIONS[0].label;

  const handleChange = async (label: string) => {
    const value = SOURCE_OPTIONS.find((option) => option.label === label)?.value;
    if (!value || value === source) return;

    setSaving(true);
    const { error } = await supabase
      .from('organisations')
      .update({ amazon_finance_source: value })
      .eq('id', organisationId);

    setSaving(false);
    if (error) {
      toast({ title: 'Failed to update Amazon Seller Central schema', variant: 'destructive' });
      return;
    }

    toast({ title: 'Amazon Seller Central schema updated', description: `Set to ${label}` });
    onUpdate();
  };

  if (tablesReady === null || !amazonFinanceSwitchVisible(tablesReady)) {
    return null;
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Amazon Seller Central schema to use</Label>
      <p className="text-sm text-muted-foreground">
        Choose which tables schema should be used for this organization: <b>Amazon&apos;s legacy Finance Events</b> tables or <b>Amazon&apos;s new Transactions</b> tables. <br /> Please note, the <b>Amazon's legacy Finance Events</b> tables will only be available until December 16th, 2026.
      </p>
      <Select value={selectedLabel} onValueChange={handleChange} disabled={saving || disabled}>
        <SelectTrigger className="w-[280px]" data-testid="amazon-finance-source">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOURCE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.label}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface AmazonSchemaReportPillProps {
  organisationId: string | undefined;
  connections: unknown;
  financeSource: string | null | undefined;
}

export const AmazonSchemaReportPill: React.FC<AmazonSchemaReportPillProps> = ({
  organisationId,
  connections,
  financeSource,
}) => {
  const hasAmazon = organisationHasAmazonSellingPartner(connections);
  const [tablesReady, setTablesReady] = useState<boolean | null>(null);

  useEffect(() => {
    if (!organisationId || !hasAmazon) {
      setTablesReady(false);
      return;
    }

    let cancelled = false;
    setTablesReady(null);
    invokeFunction<{ tablesReady?: boolean }>(supabase, 'amazon-finance-readiness-v1', {
      body: { organisation_id: organisationId },
    })
      .then(({ data }) => {
        if (!cancelled) setTablesReady(data?.tablesReady === true);
      })
      .catch(() => {
        if (!cancelled) setTablesReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId, hasAmazon]);

  if (!hasAmazon || tablesReady === null || !amazonFinanceSwitchVisible(tablesReady)) {
    return null;
  }

  return (
    <Badge
      variant="outline"
      data-testid="amazon-schema-report-pill"
      className="text-xs text-muted-foreground bg-muted/40 border-border"
    >
      {amazonSchemaReportPillLabel(financeSource)}
    </Badge>
  );
};
